/**
 * Canonical transcription-provider catalog — the SINGLE source of truth for the
 * set of batch + streaming transcription backends, their display labels, env
 * vars, and streaming capability.
 *
 * Lives in `shared/` (not backend-only) because the frontend settings UI renders
 * its own provider pickers / fallback-chain editors and must agree with the
 * backend on the exact provider set. Both sides import these tuples; neither
 * hardcodes a provider list. (4.6)
 *
 * Adding a transcription provider = one entry here + the adapter wiring in
 * `backend/src/transcribe/index.ts`; the adapter-map keys are typechecked
 * against these tuples, and TTranscriptionProvider / ENV_MAP / settings
 * validation / labels all derive from them.
 */

/** Role a batch engine may play in the ensemble archive pass. */
export type TEnsembleRole = "words" | "turns";

export interface ITranscriptionProviderMeta {
	/** Stable provider id used as the adapter-map key, setting prefix, and ledger tag. */
	readonly id: string;
	/** Human-readable label for the batch backend picker. */
	readonly label: string;
	/** Env var that holds the platform API key (`""` for the keyless local backend). */
	readonly envVar: string;
	/** Whether this provider has a real-time streaming adapter. */
	readonly supportsStreaming: boolean;
	/** Streaming-specific label (only meaningful when supportsStreaming). */
	readonly streamingLabel?: string;
	/** Ensemble archive-pass roles this engine may fill. Empty = out of the ensemble. */
	readonly ensembleRoles: readonly TEnsembleRole[];
}

/**
 * Batch backends (process complete audio files). Order is informational only —
 * the runtime fallback order is a separate, operator-configurable setting.
 */
export const TRANSCRIPTION_PROVIDERS = [
	{
		id: "local",
		label: "Local (whisply+vad)",
		envVar: "",
		supportsStreaming: false,
		// Local whisply+vad stays out of the ensemble.
		ensembleRoles: [],
	},
	{
		id: "deepgram",
		label: "Deepgram Nova-3",
		envVar: "DEEPGRAM_API_KEY",
		supportsStreaming: true,
		streamingLabel: "Deepgram (streaming)",
		ensembleRoles: ["words", "turns"],
	},
	{
		id: "assemblyai",
		label: "AssemblyAI",
		envVar: "ASSEMBLYAI_API_KEY",
		supportsStreaming: true,
		streamingLabel: "AssemblyAI (streaming)",
		ensembleRoles: ["words", "turns"],
	},
	{
		id: "azure",
		label: "Azure Speech",
		envVar: "AZURE_SPEECH_KEY",
		supportsStreaming: false,
		// Classic Azure Speech stays out of the ensemble.
		ensembleRoles: [],
	},
	{
		id: "mai",
		label: "MAI-Transcribe-2 (Azure)",
		envVar: "AZURE_MAI_SPEECH_KEY",
		supportsStreaming: false,
		// MAI diarization fails above ~20 min, so it is a words engine only.
		ensembleRoles: ["words"],
	},
	// azure-llm shares MAI's env var: one North Europe Speech resource drives both.
	{
		id: "azure-llm",
		label: "Azure LLM-speech",
		envVar: "AZURE_MAI_SPEECH_KEY",
		supportsStreaming: false,
		ensembleRoles: ["words", "turns"],
	},
	{
		id: "speechmatics",
		label: "Speechmatics Ursa 2",
		envVar: "SPEECHMATICS_API_KEY",
		supportsStreaming: true,
		streamingLabel: "Speechmatics (streaming)",
		// Speechmatics over-splits speakers, so it may supply words, never turns.
		ensembleRoles: ["words"],
	},
	{
		id: "soniox",
		label: "Soniox",
		envVar: "SONIOX_API_KEY",
		supportsStreaming: true,
		streamingLabel: "Soniox (streaming)",
		ensembleRoles: ["words"],
	},
] as const satisfies readonly ITranscriptionProviderMeta[];

/** Union of every batch transcription provider id. */
export type TTranscriptionProvider = (typeof TRANSCRIPTION_PROVIDERS)[number]["id"];

/** Providers that have a real-time streaming adapter. */
export const STREAMING_TRANSCRIPTION_PROVIDERS = TRANSCRIPTION_PROVIDERS.filter(
	(p): p is (typeof TRANSCRIPTION_PROVIDERS)[number] & { supportsStreaming: true } => p.supportsStreaming,
);

/** Union of every streaming transcription provider id. */
export type TStreamingTranscriptionProvider = (typeof STREAMING_TRANSCRIPTION_PROVIDERS)[number]["id"];

/** All batch provider ids, as a plain string array (settings validation, pickers). */
export const TRANSCRIPTION_PROVIDER_IDS: readonly TTranscriptionProvider[] = TRANSCRIPTION_PROVIDERS.map((p) => p.id);

/** All streaming provider ids, as a plain string array. */
export const STREAMING_TRANSCRIPTION_PROVIDER_IDS: readonly TStreamingTranscriptionProvider[] =
	STREAMING_TRANSCRIPTION_PROVIDERS.map((p) => p.id);

/** Providers that resolve an API key (everything except keyless `local`). */
export const KEYED_TRANSCRIPTION_PROVIDERS = TRANSCRIPTION_PROVIDERS.filter((p) => p.envVar !== "");

/** Batch backend labels keyed by id. */
export const TRANSCRIPTION_LABELS: Readonly<Record<TTranscriptionProvider, string>> = Object.fromEntries(
	TRANSCRIPTION_PROVIDERS.map((p) => [p.id, p.label]),
) as Record<TTranscriptionProvider, string>;

/** Streaming backend labels keyed by id. */
export const STREAMING_TRANSCRIPTION_LABELS: Readonly<Record<TStreamingTranscriptionProvider, string>> =
	Object.fromEntries(STREAMING_TRANSCRIPTION_PROVIDERS.map((p) => [p.id, p.streamingLabel])) as Record<
		TStreamingTranscriptionProvider,
		string
	>;

/** Type guard: is `p` a known streaming transcription provider id? */
export function isStreamingTranscriptionProvider(p: string): p is TStreamingTranscriptionProvider {
	return STREAMING_TRANSCRIPTION_PROVIDER_IDS.some((id) => id === p);
}

/**
 * Second-pass speaker-turn providers for two-pass transcription (primary words +
 * a separate diarizer's turns). Two-pass is the opt-out / fallback (workspace
 * `transcription_mode=two-pass`, or automatic when no ensemble words engine has
 * a key). `same` = skip the second pass and keep the primary provider's own
 * speaker labels. The 2026-09-05 bake-off ranked `azure-llm` first and
 * `assemblyai` second; the CODE default stays `deepgram`
 * (`DEFAULT_DIARIZATION_PROVIDER`) — operators flip it with the
 * `default_diarization_provider` setting, no deploy.
 */
export const DIARIZATION_PROVIDERS = ["same", "deepgram", "local", "mai", "azure-llm", "assemblyai"] as const;

/** Union of every diarization-provider setting value. */
export type TDiarizationProvider = (typeof DIARIZATION_PROVIDERS)[number];

/** Type guard: is `p` a known diarization-provider setting value? */
export function isDiarizationProvider(p: string): p is TDiarizationProvider {
	return DIARIZATION_PROVIDERS.some((id) => id === p);
}

function hasEnsembleRole(roles: readonly TEnsembleRole[], role: TEnsembleRole): boolean {
	return roles.some((r) => r === role);
}

/** Batch provider ids that may supply words in the ensemble archive pass. */
export const WORDS_CAPABLE_PROVIDERS: readonly TTranscriptionProvider[] = TRANSCRIPTION_PROVIDERS.filter((p) =>
	hasEnsembleRole(p.ensembleRoles, "words"),
).map((p) => p.id);

/** Batch provider ids that may supply speaker turns in the ensemble archive pass. */
export const TURNS_CAPABLE_PROVIDERS: readonly TTranscriptionProvider[] = TRANSCRIPTION_PROVIDERS.filter((p) =>
	hasEnsembleRole(p.ensembleRoles, "turns"),
).map((p) => p.id);

/**
 * How a finished transcript is produced. Ensemble is the default since
 * 2026-09-06; two-pass is the per-workspace / platform opt-out.
 */
export const TRANSCRIPTION_MODES = ["two-pass", "ensemble"] as const;

/** Union of every transcription-mode setting value. */
export type TTranscriptionMode = (typeof TRANSCRIPTION_MODES)[number];

/** Type guard: is `p` a known transcription-mode setting value? */
export function isTranscriptionMode(p: string): p is TTranscriptionMode {
	return TRANSCRIPTION_MODES.some((id) => id === p);
}

/** How the ensemble merge may rewrite text: vote among engine spans, or a free rewrite. */
export const ENSEMBLE_MERGE_MODES = ["constrained", "rewrite"] as const;

/** Union of every ensemble-merge-mode setting value. */
export type TEnsembleMergeMode = (typeof ENSEMBLE_MERGE_MODES)[number];

/** Type guard: is `p` a known ensemble-merge-mode setting value? */
export function isEnsembleMergeMode(p: string): p is TEnsembleMergeMode {
	return ENSEMBLE_MERGE_MODES.some((id) => id === p);
}

/** Which way to break a tied speaker-count vote. */
export const ENSEMBLE_HEADCOUNT_TIES = ["lower", "higher"] as const;

/** Union of every ensemble-headcount-tie setting value. */
export type TEnsembleHeadcountTie = (typeof ENSEMBLE_HEADCOUNT_TIES)[number];

/** Type guard: is `p` a known ensemble-headcount-tie setting value? */
export function isEnsembleHeadcountTie(p: string): p is TEnsembleHeadcountTie {
	return ENSEMBLE_HEADCOUNT_TIES.some((id) => id === p);
}

/** Hardcoded floor for the ensemble words-engine list. */
export const DEFAULT_ENSEMBLE_WORD_ENGINES = ["soniox", "mai", "assemblyai"] as const;

/** Hardcoded floor for the ensemble turns-engine list. */
export const DEFAULT_ENSEMBLE_TURN_ENGINES = ["azure-llm", "assemblyai", "deepgram"] as const;

export type TValidateEngineListResult =
	| { ok: true; engines: TTranscriptionProvider[] }
	| { ok: false; error: string };

function capableProviders(role: TEnsembleRole): readonly TTranscriptionProvider[] {
	return role === "words" ? WORDS_CAPABLE_PROVIDERS : TURNS_CAPABLE_PROVIDERS;
}

function allowedIdsPhrase(role: TEnsembleRole): string {
	return capableProviders(role).join(", ");
}

/**
 * Parse a JSON array of provider ids for an ensemble role.
 *
 * Every id must be in that role's capable set, with no duplicates. Words lists
 * are 1–3 entries; turns lists are 2–4 (2 is legal — the resolver applies the
 * headcount-tie policy then).
 */
export function validateEngineList(role: TEnsembleRole, raw: string): TValidateEngineListResult {
	const capable = capableProviders(role);
	const allowed = allowedIdsPhrase(role);

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return { ok: false, error: `must be a JSON array of [${allowed}]` };
	}
	if (!Array.isArray(parsed)) {
		return { ok: false, error: `must be a JSON array of [${allowed}]` };
	}

	const engines: TTranscriptionProvider[] = [];
	const seen = new Set<string>();
	for (const item of parsed) {
		if (typeof item !== "string") {
			return { ok: false, error: `must be a JSON array of [${allowed}]` };
		}
		if (seen.has(item)) {
			return { ok: false, error: `duplicate engine id "${item}" (allowed: ${allowed})` };
		}
		seen.add(item);
		const match = capable.find((id) => id === item);
		if (match === undefined) {
			return { ok: false, error: `"${item}" is not a ${role} engine (allowed: ${allowed})` };
		}
		engines.push(match);
	}

	const min = role === "words" ? 1 : 2;
	const max = role === "words" ? 3 : 4;
	if (engines.length < min || engines.length > max) {
		return { ok: false, error: `${role} engines must have ${min} to ${max} entries (allowed: ${allowed})` };
	}

	return { ok: true, engines };
}
